import { Alert, Button, Modal, Stack, Text } from "@assetwatch/ui";
import { useTranslation } from "react-i18next";
import { useCreateMarketplaceRequest } from "../../mutations/useCreateMarketplaceRequest";
import type { SelectedMarketplaceRequest } from "../../types/marketplace";

type CapabilityRequestModalProps = {
  externalCustomerId: string;
  request: SelectedMarketplaceRequest | null;
  onClose: () => void;
};

export function CapabilityRequestModal({
  externalCustomerId,
  request,
  onClose,
}: CapabilityRequestModalProps) {
  const { t } = useTranslation();
  const requestMutation = useCreateMarketplaceRequest();
  const actionLabel =
    request?.requestType === "expansion"
      ? t("marketplace.expandCoverage")
      : t("marketplace.requestInformation");
  const close = () => {
    requestMutation.reset();
    onClose();
  };

  return (
    <Modal
      open={request !== null}
      onOpenChange={(open: boolean) => {
        if (!open) close();
      }}
      title={
        requestMutation.isSuccess
          ? t("marketplace.requestSubmittedTitle")
          : actionLabel
      }
      description={request?.description}
      footer={
        requestMutation.isSuccess ? (
          <Button onClick={close}>{t("common.close")}</Button>
        ) : (
          <div className="flex justify-end gap-(--spacing-8)">
            <Button variant="outline" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={requestMutation.isPending}
              onClick={() => {
                if (!request) return;
                requestMutation.submit({
                  externalCustomerId,
                  capabilityId: request.id,
                  requestType: request.requestType,
                });
              }}
            >
              {actionLabel}
            </Button>
          </div>
        )
      }
    >
      {request ? (
        <Stack gap={16}>
          {requestMutation.isSuccess ? (
            <Alert tone="positive" title={t("marketplace.requestRecorded")} />
          ) : (
            <Text>
              {request.requestType === "expansion"
                ? t("marketplace.expansionPrompt")
                : t("marketplace.informationPrompt", {
                    subscriptionName: request.name,
                  })}
            </Text>
          )}
          {requestMutation.isError ? (
            <Alert tone="negative" title={t("marketplace.requestFailed")} />
          ) : null}
          {request.requestType === "information" && request.whyRecommended ? (
            <Alert
              tone="informative"
              title={t("marketplace.whyWeRecommendThis")}
              subtext={request.whyRecommended}
            />
          ) : null}
          {request.requestType === "information" &&
          request.eligible !== undefined ? (
            <Text size="sm" tone="subtle">
              {t("marketplace.eligibility", {
                status: request.eligible
                  ? t("marketplace.eligible")
                  : t("marketplace.notEligible"),
              })}
            </Text>
          ) : null}
        </Stack>
      ) : null}
    </Modal>
  );
}
